import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "../../../lib/auth";
import {
  fetchKnowledgeChunks,
  createKnowledgeChunk,
  updateKnowledgeChunk,
  deleteKnowledgeChunk,
  type KnowledgeChunk,
} from "../chatApi";

function shortDate(value: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return format(d, "MMM d, yyyy h:mm a");
}

function SourceBadge({ source }: { source: string | null }) {
  if (source === "hq-manual") {
    return (
      <span className="inline-block rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-semibold text-white">
        HQ
      </span>
    );
  }
  return (
    <span className="inline-block rounded-full bg-zinc-600 px-2 py-0.5 text-xs font-semibold text-white">
      Import
    </span>
  );
}

function ChunkForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial?: string;
  onSave: (content: string) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [content, setContent] = useState(initial ?? "");

  return (
    <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
      <textarea
        className="w-full resize-y rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white placeholder:text-tribal-muted focus:border-tribal-red focus:outline-none"
        rows={6}
        placeholder="Enter knowledge base content&#10;&#10;Tip: format as Q&A blocks for best chatbot matching.&#10;Q: What are your hours?&#10;A: We are open Tuesday-Sunday, 12pm-10pm."
        value={content}
        onChange={(e) => setContent(e.target.value)}
        autoFocus
      />
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={!content.trim() || saving}
          onClick={() => onSave(content)}
          className="flex items-center gap-1.5 rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80 disabled:opacity-40"
        >
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {initial !== undefined ? "Update" : "Add to Knowledge Base"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-tribal-border px-3 py-1.5 text-sm text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function DeleteConfirm({
  chunk,
  onConfirm,
  onCancel,
  deleting,
}: {
  chunk: KnowledgeChunk;
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="mx-4 w-full max-w-md rounded-xl border border-tribal-border bg-tribal-surface p-6">
        <h3 className="text-sm font-semibold text-white">Delete Knowledge Chunk</h3>
        <p className="mt-2 text-sm text-tribal-muted">
          This will permanently remove this content from the knowledge base. The chatbot
          and voice agent will no longer have access to this information.
        </p>
        <div className="mt-3 rounded-md bg-black/30 p-3">
          <p className="line-clamp-3 text-xs text-tribal-muted">{chunk.content}</p>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-tribal-border px-3 py-1.5 text-sm text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={deleting}
            onClick={onConfirm}
            className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-500 disabled:opacity-40"
          >
            {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function KnowledgeBaseView() {
  const queryClient = useQueryClient();
  // The gateway rejects KB writes from non-admins. Everyone with the chat
  // module still needs to read the KB to see what the bot is answering with.
  const { me } = useAuth();
  const isAdmin = !!me?.is_admin;
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingChunk, setEditingChunk] = useState<KnowledgeChunk | null>(null);
  const [deletingChunk, setDeletingChunk] = useState<KnowledgeChunk | null>(null);

  const limit = 25;

  const q = useQuery({
    queryKey: ["chat", "kb", debouncedSearch, page],
    queryFn: () =>
      fetchKnowledgeChunks({
        limit,
        offset: page * limit,
        search: debouncedSearch || undefined,
      }),
  });

  const createMut = useMutation({
    mutationFn: (content: string) => createKnowledgeChunk(content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "kb"] });
      setShowAdd(false);
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) =>
      updateKnowledgeChunk(id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "kb"] });
      setEditingChunk(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteKnowledgeChunk(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "kb"] });
      setDeletingChunk(null);
    },
  });

  // Debounce search
  const handleSearch = (val: string) => {
    setSearch(val);
    clearTimeout((handleSearch as { _t?: ReturnType<typeof setTimeout> })._t);
    (handleSearch as { _t?: ReturnType<typeof setTimeout> })._t = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(0);
    }, 400);
  };

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading knowledge base...
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className="m-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          Failed to load knowledge base: {(q.error as Error).message}
        </div>
      </div>
    );
  }

  const { rows, total } = q.data!;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-tribal-muted" />
          <input
            type="text"
            placeholder="Search knowledge base..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full rounded-md border border-tribal-border bg-black/30 py-1.5 pl-8 pr-3 text-sm text-white placeholder:text-tribal-muted focus:border-tribal-red focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(""); setDebouncedSearch(""); setPage(0); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-tribal-muted hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => { setShowAdd(true); setEditingChunk(null); }}
            className="flex items-center gap-1.5 rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80"
          >
            <Plus className="h-4 w-4" />
            Add Chunk
          </button>
        )}
        <span className="text-xs text-tribal-muted">
          {total} chunk{total !== 1 ? "s" : ""}
        </span>
        {!isAdmin && (
          <span className="text-xs text-tribal-muted">
            View only — ask an admin to edit the knowledge base.
          </span>
        )}
      </div>

      {/* Add form */}
      {showAdd && (
        <ChunkForm
          onSave={(content) => createMut.mutate(content)}
          onCancel={() => setShowAdd(false)}
          saving={createMut.isPending}
        />
      )}
      {createMut.isError && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200">
          Failed to create: {(createMut.error as Error).message}
        </div>
      )}

      {/* Chunk list */}
      <div className="rounded-lg border border-tribal-border bg-tribal-surface">
        <div className="divide-y divide-tribal-border">
          {rows.length === 0 && (
            <p className="py-8 text-center text-sm text-tribal-muted">
              {debouncedSearch ? "No chunks match your search" : "Knowledge base is empty"}
            </p>
          )}
          {rows.map((chunk) => (
            <div key={chunk.id} className="px-4 py-3">
              {editingChunk?.id === chunk.id ? (
                <>
                  <ChunkForm
                    initial={chunk.content}
                    onSave={(content) =>
                      updateMut.mutate({ id: chunk.id, content })
                    }
                    onCancel={() => setEditingChunk(null)}
                    saving={updateMut.isPending}
                  />
                  {updateMut.isError && (
                    <div className="mt-2 rounded-md border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200">
                      Failed to update: {(updateMut.error as Error).message}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="whitespace-pre-wrap text-sm text-white">
                      {chunk.content}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-tribal-muted">
                      <SourceBadge source={chunk.source_url} />
                      <span>Created {shortDate(chunk.created_at)}</span>
                      {chunk.updated_at &&
                        chunk.updated_at !== chunk.created_at && (
                          <span>Updated {shortDate(chunk.updated_at)}</span>
                        )}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => { setEditingChunk(chunk); setShowAdd(false); }}
                        title="Edit"
                        className="rounded p-1.5 text-tribal-muted transition-colors hover:bg-white/5 hover:text-white"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeletingChunk(chunk)}
                        title="Delete"
                        className="rounded p-1.5 text-tribal-muted transition-colors hover:bg-white/5 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-md border border-tribal-border px-3 py-1.5 text-sm text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-xs text-tribal-muted">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-md border border-tribal-border px-3 py-1.5 text-sm text-tribal-muted transition-colors hover:bg-tribal-border hover:text-white disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {/* Delete confirmation */}
      {deletingChunk && (
        <DeleteConfirm
          chunk={deletingChunk}
          onConfirm={() => deleteMut.mutate(deletingChunk.id)}
          onCancel={() => setDeletingChunk(null)}
          deleting={deleteMut.isPending}
        />
      )}
    </div>
  );
}
